import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { BlockStore, BlockCard, GridPosition } from '@/components/shared/dnd-block/types';

export type DndCard<T extends string = string> = BlockCard<T>;

interface DndState<T extends string = string> {
  // State
  dockCards: T[];
  cards: DndCard<T>[];
  gridColumns: number;

  // Actions
  addCard: (cardType: T, position: GridPosition) => void;
  removeCard: (cardId: string) => void;
  moveCard: (cardId: string, newPosition: GridPosition) => void;
  reset: () => void;
  isCardOnBoard: (cardType: T) => boolean;
  getNextGridPosition: () => GridPosition;
  getAvailableCardTypes: () => T[];
}

/**
 * Factory function to create a generic drag-and-drop store
 * @param namespace - Unique namespace for localStorage scoping (e.g., 'company-dashboard', 'employee-dashboard')
 * @param defaultDockCards - Array of card types that should be available in the dock
 * @param defaultPlacedCards - Optional array of card types that should be placed on the board by default
 * @returns A hook that provides a typed BlockStore instance
 * 
 * @example
 * // In company-dashboard.tsx
 * type CompanyCardType = 'attendance' | 'location';
 * const useCompanyDndStore = createDndStore<CompanyCardType>(
 *   'company-dashboard',
 *   ['attendance', 'location'],
 *   ['attendance'] // This card will be placed on board by default
 * );
 */
export function createDndStore<T extends string>(
  namespace: string,
  defaultDockCards: T[],
  defaultPlacedCards: T[] = [],
) {
  // Generate initial cards from defaultPlacedCards
  const initialCards: DndCard<T>[] = defaultPlacedCards.map((cardType, index) => ({
    id: `${cardType}-default-${index}`,
    type: cardType,
    position: { row: Math.floor(index / 3), col: index % 3 },
  }));

  const store = create<DndState<T>>()(
    persist(
      (set, get) => ({
        // Initial state
        dockCards: defaultDockCards,
        cards: initialCards,
        gridColumns: 3,

        // Actions
        addCard: (cardType, position) => {
          const state = get();
          // Check if card type already exists on board
          if (state.isCardOnBoard(cardType)) {
            return;
          }

          const newCard: DndCard<T> = {
            id: `${cardType}-${Date.now()}`,
            type: cardType,
            position,
          };

          set((state) => ({
            cards: [...state.cards, newCard],
          }));
        },

        removeCard: (cardId) => {
          set((state) => ({
            cards: state.cards.filter((card) => card.id !== cardId),
          }));
        },

        moveCard: (cardId, newPosition) => {
          set((state) => ({
            cards: state.cards.map((card) =>
              card.id === cardId ? { ...card, position: newPosition } : card,
            ),
          }));
        },

        reset: () => {
          set({
            cards: [],
          });
        },

        isCardOnBoard: (cardType) => {
          const state = get();
          return state.cards.some((card) => card.type === cardType);
        },

        getNextGridPosition: () => {
          const state = get();
          const existingCards = state.cards;
          const gridCols = state.gridColumns;

          if (existingCards.length === 0) {
            return { row: 0, col: 0 };
          }

          // Find the next available position
          const positions = existingCards.map((card) => card.position);
          let row = 0;
          let col = 0;

          // Find first empty position
          while (positions.some((p) => p.row === row && p.col === col)) {
            col++;
            if (col >= gridCols) {
              col = 0;
              row++;
            }
          }

          return { row, col };
        },

        getAvailableCardTypes: () => {
          return defaultDockCards;
        },
      }),
      {
        name: `dnd-storage-${namespace}`, // Scoped localStorage key
        storage: createJSONStorage(() => localStorage),
      },
    ),
  );

  // Adapter hook to make dnd store compatible with BlockStore interface
  // This hook ensures reactive updates when store state changes
  return function useDndBlockStore(): BlockStore<T> {
    const cards = store((state) => state.cards);
    const addCard = store((state) => state.addCard);
    const removeCard = store((state) => state.removeCard);
    const isCardOnBoard = store((state) => state.isCardOnBoard);
    const getNextGridPosition = store((state) => state.getNextGridPosition);
    const getAvailableCardTypes = store((state) => state.getAvailableCardTypes);
    const moveCard = store((state) => state.moveCard);

    return {
      cards: cards,
      addCard: addCard,
      removeCard: removeCard,
      moveCard: moveCard,
      isCardOnBoard: isCardOnBoard,
      getNextPosition: getNextGridPosition,
      getAvailableCardTypes: getAvailableCardTypes,
    };
  };
}

